/* Citydesign atlas: stable selection, screen-sized markers and section cleanup. */
(() => {
  if (window.__weltkarteAtlasLoaded) return;
  window.__weltkarteAtlasLoaded = true;
  const instances = new WeakMap();
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const coordinates = (marker) => {
    const parsed = {};
    const pattern = /(\d+(?:[.,]\d+)?)\s*°(?:\s*(\d+(?:[.,]\d+)?)\s*[′'’])?(?:\s*(\d+(?:[.,]\d+)?)\s*[″"])?\s*([NSEW])/gi;
    for (const match of marker.dataset.coordinates.matchAll(pattern)) {
      const direction = match[4].toUpperCase();
      const number = (value) => Number((value || '0').replace(',', '.'));
      const value = (number(match[1]) + number(match[2]) / 60 + number(match[3]) / 3600) * ('SW'.includes(direction) ? -1 : 1);
      parsed['NS'.includes(direction) ? 'latitude' : 'longitude'] = value;
    }
    for (const key of ['latitude', 'longitude']) {
      if (parsed[key] === undefined && marker.dataset[key]?.trim()) parsed[key] = Number(marker.dataset[key]);
    }
    if (!Number.isFinite(parsed.latitude) || !Number.isFinite(parsed.longitude) || Math.abs(parsed.latitude) > 90 || Math.abs(parsed.longitude) > 180) return null;
    return parsed;
  };

  // Natural Earth I projection, matching the bundled SVG (d3-geo, ISC license).
  const project = ({ latitude, longitude }, stage) => {
    const phi = latitude * Math.PI / 180;
    const lambda = longitude * Math.PI / 180;
    const phi2 = phi * phi;
    const phi4 = phi2 * phi2;
    const x = lambda * (.8707 - .131979 * phi2 + phi4 * (phi4 * (.003971 * phi2 - .001529 * phi4) - .013791));
    const y = phi * (1.007226 + phi2 * (.015085 + phi4 * (.028874 * phi2 - .044475 - .005916 * phi4)));
    const scale = Number(stage.dataset.projectionScale);
    return [(Number(stage.dataset.projectionX) + scale * x) / 720 * 100, (Number(stage.dataset.projectionY) - scale * y) / 350 * 100];
  };

  const init = (root) => {
    if (instances.has(root)) return;
    const tabs = Array.from(root.querySelectorAll('[data-weltkarte-point]'));
    const panels = Array.from(root.querySelectorAll('[data-weltkarte-panel]'));
    if (!tabs.length || !panels.length) return;
    const abort = new AbortController();
    const on = (target, type, handler, options = {}) => target?.addEventListener(type, handler, { ...options, signal: abort.signal });
    const markers = Array.from(root.querySelectorAll('[data-weltkarte-marker]'));
    const surface = root.querySelector('[data-weltkarte-zoom-surface]');
    const stage = root.querySelector('[data-weltkarte-zoom-stage]');
    const selection = root.querySelector('[data-weltkarte-selection]');
    const zoomIn = root.querySelector('[data-weltkarte-zoom-in]');
    const zoomOut = root.querySelector('[data-weltkarte-zoom-out]');
    const reset = root.querySelector('[data-weltkarte-zoom-reset]');
    let activeId = root.dataset.weltkarteInitial || tabs[0].dataset.weltkartePoint;
    let zoom = 1;
    let translateX = 0;
    let translateY = 0;
    let frame = null;
    let drag = null;

    markers.forEach((marker) => {
      if (!stage?.hasAttribute('data-weltkarte-atlas')) return;
      const position = coordinates(marker);
      if (!position) { marker.hidden = true; return; }
      const [x, y] = project(position, stage);
      marker.style.left = `${x}%`;
      marker.style.top = `${y}%`;
    });

    const update = () => {
      frame = null;
      if (!surface || !stage) return;
      const maxX = Math.max(0, (stage.offsetWidth * zoom - surface.clientWidth) / 2);
      const maxY = Math.max(0, (stage.offsetHeight * zoom - surface.clientHeight) / 2);
      translateX = clamp(translateX, -maxX, maxX);
      translateY = clamp(translateY, -maxY, maxY);
      stage.style.transform = `translate(${translateX}px, ${translateY}px) scale(${zoom})`;
      root.style.setProperty('--weltkarte-inverse-zoom', 1 / zoom);
      surface.classList.toggle('is-zoomed', zoom > 1);
      if (zoomIn) zoomIn.disabled = zoom >= 3;
      if (zoomOut) zoomOut.disabled = zoom <= 1;
    };
    const schedule = () => { if (frame === null) frame = requestAnimationFrame(update); };
    const focusMarker = () => {
      const marker = markers.find((item) => item.dataset.weltkarteMarker === activeId);
      if (!marker || marker.hidden || zoom === 1 || !stage) return;
      translateX = (stage.offsetWidth / 2 - marker.offsetLeft) * zoom;
      translateY = (stage.offsetHeight / 2 - marker.offsetTop) * zoom;
    };
    const activate = (id, focus = false) => {
      activeId = id;
      tabs.forEach((tab) => {
        const active = tab.dataset.weltkartePoint === id;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active) {
          if (selection) selection.textContent = tab.dataset.cityName;
          if (focus) tab.focus({ preventScroll: true });
        }
      });
      panels.forEach((panel) => {
        const active = panel.dataset.weltkartePanel === id;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
      markers.forEach((marker) => marker.classList.toggle('is-active', marker.dataset.weltkarteMarker === id));
      focusMarker();
      schedule();
    };
    tabs.forEach((tab, index) => {
      on(tab, 'click', () => activate(tab.dataset.weltkartePoint));
      on(tab, 'keydown', (event) => {
        const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
        let target;
        if (step) target = (index + step + tabs.length) % tabs.length;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = tabs.length - 1;
        else return;
        event.preventDefault();
        activate(tabs[target].dataset.weltkartePoint, true);
      });
    });
    const setZoom = (next) => {
      const value = clamp(next, 1, 3);
      const ratio = value / zoom;
      translateX *= ratio;
      translateY *= ratio;
      zoom = value;
      schedule();
    };
    on(zoomIn, 'click', () => setZoom(zoom + .25));
    on(zoomOut, 'click', () => setZoom(zoom - .25));
    on(reset, 'click', () => { zoom = 1; translateX = 0; translateY = 0; schedule(); });
    on(stage, 'dragstart', (event) => event.preventDefault());
    on(surface, 'wheel', (event) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      setZoom(zoom + (event.deltaY > 0 ? -.1 : .1));
    }, { passive: false });
    on(surface, 'pointerdown', (event) => {
      // Touch scroll belongs to the page; explicit zoom controls work on mobile.
      if (event.pointerType === 'touch' || event.button !== 0 || zoom <= 1) return;
      drag = { pointer: event.pointerId, x: event.clientX, y: event.clientY, tx: translateX, ty: translateY };
      try { surface.setPointerCapture(event.pointerId); } catch { /* capture is optional */ }
      surface.classList.add('is-dragging');
    });
    on(surface, 'pointermove', (event) => {
      if (!drag || drag.pointer !== event.pointerId) return;
      translateX = drag.tx + event.clientX - drag.x;
      translateY = drag.ty + event.clientY - drag.y;
      schedule();
    });
    const stopDrag = (event) => {
      if (!drag || drag.pointer !== event.pointerId) return;
      drag = null;
      surface.classList.remove('is-dragging');
      try { if (surface.hasPointerCapture(event.pointerId)) surface.releasePointerCapture(event.pointerId); } catch { /* capture is optional */ }
    };
    on(surface, 'pointerup', stopDrag);
    on(surface, 'pointercancel', stopDrag);
    on(surface, 'lostpointercapture', stopDrag);
    const observer = surface ? new ResizeObserver(schedule) : null;
    observer?.observe(surface);
    on(stage?.querySelector('img'), 'load', schedule);
    instances.set(root, {
      activate,
      destroy: () => {
        abort.abort();
        observer?.disconnect();
        if (frame !== null) cancelAnimationFrame(frame);
        instances.delete(root);
      }
    });
    activate(tabs.some((tab) => tab.dataset.weltkartePoint === activeId) ? activeId : tabs[0].dataset.weltkartePoint);
  };
  const roots = (scope) => [...(scope.matches?.('[data-weltkarte]') ? [scope] : []), ...scope.querySelectorAll('[data-weltkarte]')];
  roots(document).forEach(init);
  document.addEventListener('shopify:section:load', (event) => roots(event.target).forEach(init));
  document.addEventListener('shopify:section:unload', (event) => roots(event.target).forEach((root) => instances.get(root)?.destroy()));
  document.addEventListener('shopify:block:select', (event) => {
    const tab = event.target.matches?.('[data-weltkarte-point]') ? event.target : event.target.querySelector('[data-weltkarte-point]');
    if (tab) instances.get(tab.closest('[data-weltkarte]'))?.activate(tab.dataset.weltkartePoint);
  });
})();
